import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SelectedDeviceService } from 'src/app/shared/device/selected-device.service';
import { PghUserDeviceBase } from 'src/app/shared/device/pgh-user-device';
import { ReceiptService } from 'src/app/core/services/receipt.service';
import { IUploadReceipt } from 'src/app/model/device-inventory/IUploadReceipt';

@Component({
  selector: 'pgh-upload-receipt',
  templateUrl: './upload-receipt-modal.component.html',
  styleUrls: ['./upload-receipt-modal.component.scss']
})
export class UploadReceiptModalComponent implements OnInit {
  constructor(
    private selectedDeviceService: SelectedDeviceService,
    private dialogRef: MatDialogRef<UploadReceiptModalComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private receiptService: ReceiptService
  ) { }

  selectedDevice: PghUserDeviceBase;
  categoryType: string = '';
  isReceiptUploadEnabled = true;
  dropzoneActive = true;
  message: string;
  isUploadComplete = false;
  isLoading = false;
  isSuccess = false;
  uploadError = '';
  currentFile: File;
  receiptUploadProgress = 0;
  allowedFileTypeNames = '';
  allowedFileTypeExtensions = '';
  uploadConfirmed = false;
  uploadPage: IUploadReceipt;
  imageString = '';
  imageName = '';
  showPdfIcon = false;

  ngOnInit() {
    this.uploadPage = this.dialogData.uploadRecieptPage;
    this.selectedDevice = this.selectedDeviceService.getSelectedDevice();
    this.allowedFileTypeNames = this.getAllowedFileTypes();
    if (this.selectedDevice.hasReceipt) {
      this.getReceipt();
    }
  }

  getReceipt() {
    this.receiptService.getReceiptImage(this.selectedDevice.deviceId, this.selectedDevice.inventoryId).subscribe({
      next: (data) => {
        this.setImageData(data);
      },
      error: (error) => {
        this.dialogRef.close();
        throw error;
      }
    })
  }

  setImageData(base64String: string) {
    const firstChar = base64String.charAt(0);
    let imageExt = this.getExtensionFromHeader(firstChar);
    if ('pdf' === imageExt) {
      this.showPdfIcon = true;
    } else {
      this.showPdfIcon = false;
    }
    this.imageString = `data:image/${imageExt};base64,${base64String}`;
    this.imageName = `${this.selectedDevice.nickName}.${imageExt}`;
  }

  getExtensionFromHeader(firstChar: string): string {
    if (firstChar === 'i') {
      return 'png';
    } else if (firstChar === '/') {
      return 'jpg';
    } else if (firstChar === 'R') {
      return 'gif';
    } else if (firstChar === 'J') {
      return 'pdf';
    }
    return null;
  }

  onCancelClicked() {
    this.uploadError = '';
    this.isLoading = false;
    this.currentFile = null;
    this.isUploadComplete = false;
    this.isSuccess = false;
    this.dialogRef.close({ data: 'cancel' });
  }

  onOkClicked() {
    this.dialogRef.close({ data: 'success' });
  }

  onSaveClicked() {
    this.receiptUploadProgress = 0;
    this.message = '';

    //Get the file Extension
    const extension = this.currentFile.name.split('.').pop();

    const reader = new FileReader();
    let image: any;
    reader.addEventListener(
      "load",
      () => {
        // convert image file to base64 string
        image = reader.result;
        image = image.split(',').pop();
        // FirstChar in file header needs to be one of our accepted file types.
        if (this.getExtensionFromHeader(image.charAt(0)) !== extension?.toLowerCase()) {
          this.uploadError = this.uploadPage.allowedFileTypesErrorMessage;
          this.currentFile = null;
          return;
        }
        this.isLoading = true;

        this.receiptService.uploadReceiptFile(this.selectedDevice.deviceId, image, extension.toLowerCase()).subscribe({
          next: (data) => {
            this.currentFile = null;
            this.isUploadComplete = true;
            this.isSuccess = true;
            this.isLoading = false;
            this.selectedDevice.hasReceipt = true;
            this.uploadConfirmed = false;
            this.setImageData(data.receipt);
          },
          error: (error) => {
            console.error(error);
            this.isLoading = false;
            this.uploadConfirmed = false;
            this.uploadError = 'Upload Failed';
            this.deleteFile();
            throw error;
          }
        });
      },
      false
    );

    reader.readAsDataURL(this.currentFile);
  }

  dropzoneState($event: boolean) {
    this.dropzoneActive = $event;
  }

  getAllowedFileTypes() {
    let fileTypes = '';
    this.uploadPage.allowedFileTypes.replace('and', ',')?.split(',').forEach(fileType => {
      fileTypes += `.${fileType.trim()},`;
    });
    this.allowedFileTypeExtensions = fileTypes;
    return this.uploadPage.allowedFileTypes;
  }

  getMaxFileSize(): number {
    return (parseInt(this.uploadPage.maxUploadFileSize) ?? 10) * 1024 * 1024;
  }

  getFileType(fileName: string) {
    let fileType = fileName.split('.')[1].toLowerCase();
    switch (fileType) {
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'pdf':
        return 'application/pdf';
      default:
        return '';
    }
  }

  isFileValid(): boolean {
    this.uploadError = '';
    if (this.currentFile.size > this.getMaxFileSize()) {
      this.uploadError = this.uploadPage.maxUploadFileSizeErrorMessage;
    }

    const extension = this.currentFile.name.split('.').pop();
    if (this.allowedFileTypeExtensions.toLowerCase().indexOf(extension.toLowerCase()) < 0) {
      this.uploadError = this.uploadPage.allowedFileTypesErrorMessage;
    }

    if (this.uploadError === '') {
      return true;
    }

    this.currentFile = null;
    return;
  }

  onFileChange(evt) {
    if (evt != undefined && evt.length > 0) {
      const file: File = evt[0];

      this.currentFile = file;
      if (!this.isFileValid()) {
        return;
      }
    } else {
      this.currentFile = null;
      this.isUploadComplete = false;
      this.isSuccess = false;
    }
  }

  /**
    * Simulate the upload process
  */
  uploadFilesSimulator(file: File) {
    setTimeout(() => {
      if (file === null || file?.size <= 0) {
        this.isUploadComplete = true;
        //this.isLoading = false;
        this.isSuccess = false;
        return;
      } else {
        const progressInterval = setInterval(() => {
          if (this.receiptUploadProgress === 100) {
            clearInterval(progressInterval);
            this.isUploadComplete = true;
            this.isSuccess = true;
          } else {
            this.receiptUploadProgress += 5;
          }
        }, 200);
      }
    }, 1000);
  }

  deleteFile() {
    this.currentFile = null;
    this.isLoading = false;
    this.isUploadComplete = false;
    this.isSuccess = false;
  }
}
